import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';

import BotsSelector from '@webclient/components/search-forms/gamification-search/actions/bots-selector';
import FileAttachment from '@webclient/components/search-forms/gamification-search/actions/file-attachment';
import FilesInRoom from '@webclient/components/search-forms/gamification-search/actions/files-in-room';
import Filters from '@webclient/components/search-forms/gamification-search/actions/filters';
import Mic from '@webclient/components/search-forms/gamification-search/actions/mic';
import ScopeSelector from '@webclient/components/search-forms/gamification-search/actions/scope-selector';
import AttachmentIcon from '@webclient/components/search-forms/gamification-search/components/attachment-icon';
import SearchBoxReactHookForm from '@webclient/components/search-forms/gamification-search/components/search-box-react-hook-form';
import useSharedUppyInstance from '@webclient/components/search-forms/gamification-search/hooks/useSharedUppyInstance';
import {
  StyledBackdrop,
  StyledDropAnywhereBox,
  StyledStack,
  StyledTextHelper,
  StyledWrapper,
} from '@webclient/components/search-forms/gamification-search/styles';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useDeleteCredentialByKey from '@webclient/hooks/useDeleteCredentialByKey';

import {
  deleteAttachmentById,
  deleteAttachmentFromRoomByKey,
} from '@webclient/state/slices/search-session';

import HandRoundedIcon from '~icons/knowz-iconify/hand-rounded';

import { SearchWidget, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

type Props = {
  origin: 'search' | 'home';
};

export default function GamificationSearch({ origin }: Props) {
  const attachments = useAppSelector(
    (state) => state.searchSession.attachments,
  );
  const dispatch = useAppDispatch();
  const { mutate: mutateCredentialByKey } = useDeleteCredentialByKey();

  const { dropTargetCallbackRef, isDragging } = useSharedUppyInstance();

  const { t } = useTranslate();

  function handleDeleteAttachmentById({
    id,
    key,
  }: {
    id: string;
    key: string | null;
  }) {
    return async () => {
      if (key === null) return;
      dispatch(deleteAttachmentById({ id }));
      dispatch(deleteAttachmentFromRoomByKey({ key }));
      mutateCredentialByKey({
        key,
      });
    };
  }

  return (
    <Box>
      <StyledWrapper
        isDragAccept={isDragging}
        ref={dropTargetCallbackRef}
      >
        {isDragging && (
          <Portal>
            <StyledBackdrop open={isDragging}>
              <StyledDropAnywhereBox>
                <StyledStack>
                  <HandRoundedIcon />
                  <Text
                    variant="textMd"
                    weight="medium"
                  >
                    {t(
                      'layout.searchWidget.dropTarget.dropHere',
                      'Drop file anywhere to search it',
                    )}
                  </Text>
                </StyledStack>
              </StyledDropAnywhereBox>
            </StyledBackdrop>
          </Portal>
        )}

        <SearchWidget>
          <SearchWidget.Actions
            leftAlignedActions={[
              <FileAttachment />,
              <ScopeSelector />,
              <Mic />,
              <FilesInRoom />,
            ]}
            rightAlignedActions={[<BotsSelector />, <Filters />]}
          />

          <SearchBoxReactHookForm
            origin={origin}
            Slot={SearchWidget.SearchBox}
          />

          <SearchWidget.FilePreviews
            files={attachments}
            renderIcon={(file) => (
              <AttachmentIcon
                onDelete={handleDeleteAttachmentById({
                  key: file.key,
                  id: file.id,
                })}
                isPending={file.isPending || file.isProcessingByAI}
                progressInPercent={file.progress}
                format={file.type}
              />
            )}
          />
          {attachments.some((file) => file.error) && (
            <Text
              variant="textXs"
              color="error"
              sx={{
                marginTop: '8px',
                textAlign: 'center',
              }}
            >
              {t(
                'layout.searchWidget.messages.proccessingAiError',
                'An error occurred while processing the file. Please try again.',
              )}
            </Text>
          )}
        </SearchWidget>
      </StyledWrapper>

      <StyledTextHelper
        variant="textXs"
        component="p"
      >
        {t(
          'layout.searchWidget.textHelper',
          'AI may produce inaccurate results, please double check all important information.',
        )}
      </StyledTextHelper>
    </Box>
  );
}
